import { useState, useEffect, useMemo } from "react";
import { produce } from "immer";
import { useCloseCondition } from "@/components/FullScreenModal";
import { documentApiService } from "@/features/documents/services/documentApiService";
import { useDocumentValidation } from "@/features/documents/hooks/useDocumentValidation";
import type { Document, LineItem } from "document-api-client";

export interface DocumentEditorProps {
    document: Document;
    onSave?: (modifiedDocument: Document) => Promise<void>;
    onApprove?: (documentId: string) => Promise<void>;
    onRevokeApprove?: (documentId: string) => Promise<void>;
    onDelete?: (documentId: string) => Promise<void>;
    onNavigate?: (direction: "next" | "prev") => void;
    isLoading?: boolean;
    hasNext?: boolean;
    hasPrevious?: boolean;
}

export function useDocumentEditor({
    document,
    onSave,
    onApprove,
    onRevokeApprove,
    onDelete,
    onNavigate,
}: DocumentEditorProps) {
    const isReadOnly = document.isApproved || false;
    const { setCanClose } = useCloseCondition();

    const [editedDocument, setEditedDocument] = useState<Document>({
        ...document,
        lineItems: document.lineItems || [],
    });
    const [originalDocument, setOriginalDocument] = useState<Document>(document);
    const [isSaving, setIsSaving] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showRevokeConfirmation, setShowRevokeConfirmation] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
    const [closeResolver, setCloseResolver] = useState<((value: boolean) => void) | null>(null);

    const { fieldErrors, hasErrors, isValid } = useDocumentValidation(editedDocument);

    const hasChanges = useMemo(() => {
        return editedDocument !== originalDocument;
    }, [editedDocument, originalDocument]);

    // Status validation functions
    const canDelete = (status: string): boolean => {
        return status === "FAILED" || status === "COMPLETED";
    };

    const canApprove = (status: string): boolean => {
        return status === "COMPLETED" && isValid;
    };

    const canSave = (status: string): boolean => {
        return status === "COMPLETED";
    };

    // Update local state when document prop changes
    useEffect(() => {
        const documentWithItems = {
            ...document,
            lineItems: document.lineItems || [],
        };
        setEditedDocument(documentWithItems);
        setOriginalDocument(documentWithItems);
    }, [document]);

    // Set up close condition for FullScreenOverlay context
    useEffect(() => {
        setCanClose(() => {
            if (hasChanges) {
                return new Promise<boolean>((resolve) => {
                    setCloseResolver(() => resolve);
                    setShowDiscardConfirmation(true);
                });
            }
            return true;
        });
    }, [setCanClose, hasChanges]);

    // Input change handlers
    const handleInputChange = <K extends keyof Document>(
        field: K,
        value: Document[K],
    ) => {
        if (isReadOnly) return;
        setEditedDocument(produce((draft) => {
            draft[field] = value;
        }));
    };

    const handleLineItemChange = <K extends keyof LineItem>(
        index: number,
        field: K,
        value: LineItem[K],
    ) => {
        if (isReadOnly) return;
        setEditedDocument(produce((draft) => {
            if (!draft.lineItems) draft.lineItems = [];
            draft.lineItems[index][field] = value;
        }));
    };

    const addLineItem = () => {
        if (isReadOnly) return;
        const newItem: LineItem = {
            item: "",
            productCode: "",
            quantity: 1,
            unitPrice: 0,
            price: 0,
            taxRate: 0,
        } as LineItem;
        setEditedDocument(produce((draft) => {
            if (!draft.lineItems) draft.lineItems = [];
            draft.lineItems.push(newItem);
        }));
    };

    const deleteDocumentItem = (index: number) => {
        if (isReadOnly) return;
        setEditedDocument(produce((draft) => {
            if (draft.lineItems) {
                draft.lineItems.splice(index, 1);
            }
        }));
    };

    const handleUndo = () => {
        if (!originalDocument) return;
        setEditedDocument(originalDocument);
    };

    // Action handlers
    const handleSave = async () => {
        if (isReadOnly) return;
        setIsSaving(true);
        try {
            await documentApiService.updateDocument(document.documentId, editedDocument);
            if (onSave) {
                await onSave(editedDocument);
            }
        } catch (error) {
            console.error("Error saving receipt:", error);
            alert("Failed to save receipt. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleApprove = async () => {
        setIsApproving(true);
        try {
            const approvedDocument = { ...editedDocument, isApproved: true };
            await documentApiService.updateDocument(document.documentId, approvedDocument);
            if (onSave) {
                await onSave(approvedDocument);
            }
            if (onApprove) {
                await onApprove(document.documentId);
            }
        } catch (error) {
            console.error("Error approving receipt:", error);
            alert("Failed to approve receipt. Please try again.");
        } finally {
            setIsApproving(false);
        }
    };

    const handleRevokeApprovalConfirm = () => {
        setShowRevokeConfirmation(true);
    };

    const handleRevokeApproval = async () => {
        setIsApproving(true);
        try {
            const unapprovedDocument = { ...editedDocument, isApproved: false };
            await documentApiService.updateDocument(document.documentId, unapprovedDocument);
            if (onSave) {
                await onSave(unapprovedDocument);
            }
            if (onRevokeApprove) {
                await onRevokeApprove(document.documentId);
            }
            setShowRevokeConfirmation(false);
        } catch (error) {
            console.error("Error revoking approval:", error);
            alert("Failed to revoke approval. Please try again.");
        } finally {
            setIsApproving(false);
        }
    };

    const handleDeleteConfirm = () => {
        setShowDeleteConfirmation(true);
    };

    const handleDelete = async () => {
        if (!onDelete) return;
        setIsDeleting(true);
        try {
            await documentApiService.deleteDocument(document.documentId);
            await onDelete(document.documentId);
            setShowDeleteConfirmation(false);
        } catch (error) {
            console.error("Error deleting receipt:", error);
            alert("Failed to delete receipt. Please try again.");
        } finally {
            setIsDeleting(false);
        }
    };

    // Discard changes handlers
    const handleDiscardChanges = (action: () => void) => {
        if (hasChanges) {
            setPendingAction(() => action);
            setShowDiscardConfirmation(true);
        } else {
            action();
        }
    };

    const confirmDiscardChanges = () => {
        pendingAction?.();
        closeResolver?.(true);
        setShowDiscardConfirmation(false);
        setPendingAction(null);
        setCloseResolver(null);
    };

    const cancelDiscardChanges = () => {
        closeResolver?.(false);
        setShowDiscardConfirmation(false);
        setPendingAction(null);
        setCloseResolver(null);
    };

    const handleNavigate = (direction: "next" | "prev") => {
        handleDiscardChanges(() => onNavigate?.(direction));
    };

    return {
        editedDocument,
        isReadOnly,
        isSaving,
        isApproving,
        isDeleting,
        showRevokeConfirmation,
        setShowRevokeConfirmation,
        showDeleteConfirmation,
        setShowDeleteConfirmation,
        showDiscardConfirmation,
        fieldErrors,
        hasErrors,
        isValid,
        hasChanges,
        canDelete,
        canApprove,
        canSave,
        handleInputChange,
        handleLineItemChange,
        addLineItem,
        deleteDocumentItem,
        handleUndo,
        handleSave,
        handleApprove,
        handleRevokeApprovalConfirm,
        handleRevokeApproval,
        handleDeleteConfirm,
        handleDelete,
        handleNavigate,
        confirmDiscardChanges,
        cancelDiscardChanges,
    };
}
